import { getBreadcrumbs } from 'server/utils/breadcrumbs/getBreadcrumbs';
import { fetchGlobalPricingId } from 'server/utils/fetchGlobalPricingId';
import { IProductLandingPage } from 'shared/types/ProductLandingPage';
import { normalizeValue } from 'shared/utils/normalizeValue';
import { PlpPageComponent as PLPLandingPageComponent } from 'product-landing-pages/components/PlpPageComponent';
import { startCase } from 'shared/utils/startCase';
import { isSearchCrawler } from 'shared/utils/isSearchCrawler';
import { setServerSideTranslations } from 'localization/utils/setServerSideTranslations';
import { getRedirectionPath } from 'localization/utils/getRedirectionPath';
import {
  FOOTWEAR_ACTIVITIES,
  TAXONOMY_LEVEL_1,
  TAXONOMY_LEVEL_2,
} from 'product-landing-pages/utils/taxonomyLevels';
import { ACTIVIITY_LIST } from 'product-landing-pages/utils/activityList';
import { getConsumerSearchServerSideData } from 'server/utils/getConsumerSearchDataServerSide';
import QueryString from 'qs';
import {
  CONSUMER_SEARCH_BRANDS_PRESENTATION,
  CONSUMER_SEARCH_BRANDS_REDIRECTS,
  CONSUMER_SEARCH_BRANDS_REVERSE_REDIRECTS,
  CONSUMER_SEARCH_BRANDS_SLUG_CORRECTIONS,
} from 'brand/enum/consumerSearchRedirects';
import { CONSUMER_SEARCH_TAXONOMY_REDIRECTS } from 'shared/utils/consumerSearchTaxononyRedirects';
import { slugify } from 'shared/utils/consumerSearchSlugify';

const CONSUMER_SEARCH_VALUES = {
  boots: 'boot',
  cleats: 'cleat',
  sandals: 'sandal',
};

const CategorizedBrandPage = ({
  brandName,
  brandDisplayName,
  breadcrumbs,
  browseKey,
  browseValue,
  category,
  constructorResponse,
  displayName,
  globalPricingId,
  subCategory,
  isCrawler = false,
  consumerSearchFilters,
  consumerSearchInitialData = '{}',
  pageBreadcrumbs,
}: IProductLandingPage & {
  consumerSearchFilters?: any;
}) => {
  const {
    name = displayName,
    description = '',
    metadataDescription = null,
    titleTag = '',
  } = breadcrumbs || {};

  return (
    <PLPLandingPageComponent
      breadcrumbs={
        pageBreadcrumbs || [
          { name: startCase(category), slug: `/${category}` },
          {
            name: brandDisplayName,
            slug: `/brand/${slugify(brandName)}`,
          },
          {
            name: startCase(subCategory),
            slug: `/${category}/${slugify(subCategory)}`,
          },
        ]
      }
      hasBreadcrumbSchema
      browseKey={browseKey}
      browseValue={browseValue}
      constructorResponse={constructorResponse}
      description={description}
      displayName={name}
      globalPricingId={globalPricingId}
      metadataDescription={metadataDescription}
      metaTitle={titleTag}
      isCrawler={isCrawler}
      consumerSearchFilters={consumerSearchFilters}
      consumerSearchInitialData={JSON.parse(consumerSearchInitialData)}
    />
  );
};

export const getServerSideProps = async ({ query, req, res, locale }) => {
  const {
    pageSlug: category,
    brandName,
    taxonomies,
  }: { pageSlug: string; brandName: string; taxonomies: string[] } = query;

  const [subCategory, taxonomyLevel3, taxonomyLevel4] = Array.isArray(taxonomies)
    ? taxonomies
    : [taxonomies];

  const q = QueryString.parse(query);
  const globalPricingId = await fetchGlobalPricingId({ req, res });

  const isCrawler = isSearchCrawler(req?.headers);

  try {
    const presentationBrandName: string =
      CONSUMER_SEARCH_BRANDS_PRESENTATION[brandName] || startCase(brandName);
    const presentationSubcategory: string = startCase(subCategory);
    const presentationTaxonomyLevel3: string = taxonomyLevel3
      ? ` ${startCase(taxonomyLevel3)}`
      : '';
    const presentationTaxonomyLevel4: string = taxonomyLevel4
      ? ` ${startCase(taxonomyLevel4)}`
      : '';
    const brandBrowseName: string = startCase(brandName, {
      ignoreSpecialDisplay: true,
    }).toLowerCase();
    const normalizedBrandName = normalizeValue(brandBrowseName);

    const newCategory = category.toLowerCase() === 'footwear' ? 'sneakers' : category.toLowerCase();

    // BUY3-1381: need to redirect special character brand names to normalized value
    // as Google is still crawling V1 GOAT
    if (normalizedBrandName !== brandBrowseName || category.toLowerCase() !== newCategory) {
      return {
        redirect: {
          destination: getRedirectionPath(
            `/${newCategory}/brand/${slugify(normalizedBrandName)}/${subCategory}${taxonomyLevel3 ? `/${taxonomyLevel3}` : ''}${
              taxonomyLevel4 ? `/${taxonomyLevel4}` : ''
            }`,
            locale,
          ),
          permanent: true,
        },
      };
    }

    // these are the queries name used by consumer search
    // this filter will be used in addition to the selected filters from the users

    let consumerSearchFilters: any = {};

    if (
      TAXONOMY_LEVEL_1.includes(category.toLowerCase()) ||
      (category.toLowerCase() === 'sneakers' &&
        TAXONOMY_LEVEL_2.includes(slugify(subCategory.toLowerCase())))
    ) {
      const isL1 = TAXONOMY_LEVEL_1.includes(category.toLowerCase());
      consumerSearchFilters = {
        brands: brandName,
        categories: !isL1 ? 'footwear' : category,
        productTypes:
          category?.toLowerCase() === 'sneakers'
            ? slugify(subCategory)
            : subCategory?.replaceAll('_', '-') || '',
      };
    } else if (TAXONOMY_LEVEL_2.includes(category.toLowerCase())) {
      if (ACTIVIITY_LIST.includes(subCategory.toLowerCase())) {
        consumerSearchFilters = {
          brands: brandName,
          productTypes: category?.replaceAll('_', '-') || '',
          activityList: CONSUMER_SEARCH_VALUES[subCategory.toLowerCase()] || subCategory,
        };
      } else {
        consumerSearchFilters = {
          brands: brandName,
          productTypes: category.replaceAll('_', '-'),
          activities: subCategory,
        };
      }
    }

    if (taxonomyLevel3) {
      consumerSearchFilters.taxonomyLevel3 = slugify(taxonomyLevel3);
    }

    if (taxonomyLevel4) {
      consumerSearchFilters.taxonomyLevel4 = slugify(taxonomyLevel4);
    }

    if ((consumerSearchFilters?.productTypes || '').toLowerCase() === 'sneakers') {
      delete consumerSearchFilters.productTypes;
      consumerSearchFilters.categories = 'footwear';
    }

    const newTaxonomyRedirect = CONSUMER_SEARCH_TAXONOMY_REDIRECTS[subCategory];
    if (newTaxonomyRedirect && category !== newTaxonomyRedirect.base) {
      if (newTaxonomyRedirect.subCategory) {
        return {
          redirect: {
            destination: getRedirectionPath(
              `/${newTaxonomyRedirect.base}/brand/${brandName}/${newTaxonomyRedirect.subCategory}${taxonomyLevel3 ? `/${taxonomyLevel3}` : ''}${
                taxonomyLevel4 ? `/${taxonomyLevel4}` : ''
              }`,
              locale,
            ),
            permanent: true,
          },
        };
      }
      return {
        redirect: {
          destination: getRedirectionPath(
            `/${newTaxonomyRedirect.base}/brand/${brandName}`,
            locale,
          ),
          permanent: true,
        },
      };
    }

    if (CONSUMER_SEARCH_BRANDS_REDIRECTS[brandName]) {
      return {
        redirect: {
          destination: getRedirectionPath(
            `/${category}/brand/${CONSUMER_SEARCH_BRANDS_REDIRECTS[brandName]}/${subCategory}${taxonomyLevel3 ? `/${taxonomyLevel3}` : ''}${
              taxonomyLevel4 ? `/${taxonomyLevel4}` : ''
            }`,
            locale,
          ),
          permanent: false,
        },
      };
    }
    let breadcrumbs = {};
    // for activity link make sure we are passing the right value to consumer search
    // LOOKS LIKE WE CAN HAVE SNEAKERS/BRAND/NIKE/BOOT

    // BE@RBRICK case handling - redirect to normalized brand name bearbrick but send beatrbrick to consumer search
    if (
      consumerSearchFilters.categories === 'footwear' &&
      consumerSearchFilters.productTypes &&
      FOOTWEAR_ACTIVITIES.includes(consumerSearchFilters.productTypes?.toLowerCase() as string)
    ) {
      if (
        ['running', 'basketball', 'skateboarding'].includes(
          consumerSearchFilters.productTypes.toLowerCase() as string,
        )
      ) {
        consumerSearchFilters = {
          ...consumerSearchFilters,
          activityList: consumerSearchFilters.productTypes,
        };
        delete consumerSearchFilters.productTypes;
      } else {
        return {
          notFound: true,
        };
      }
    }

    try {
      breadcrumbs = await getBreadcrumbs({
        req,
        res,
        path: `${category}/brand/${CONSUMER_SEARCH_BRANDS_REVERSE_REDIRECTS[brandName] || brandName}/${
          subCategory
        }${taxonomyLevel3 ? `/${taxonomyLevel3}` : ''}${taxonomyLevel4 ? `/${taxonomyLevel4}` : ''}`,
      });
    } catch {
      breadcrumbs = {};
    }

    if (CONSUMER_SEARCH_BRANDS_SLUG_CORRECTIONS[brandName]) {
      consumerSearchFilters.brands = CONSUMER_SEARCH_BRANDS_SLUG_CORRECTIONS[brandName];
    }

    // If consumer search is enabled, we need to fetch the consumer data details
    const consumerSearchInitialData = await getConsumerSearchServerSideData({
      filters: {
        ...q,
        pageFilters: consumerSearchFilters,
      },
    });

    if (!consumerSearchInitialData?.items?.hasData) {
      return { notFound: true };
    }

    const CORRECT_BRAND_NAME = CONSUMER_SEARCH_BRANDS_REVERSE_REDIRECTS[brandName] || brandName;

    return {
      props: {
        ...(await setServerSideTranslations({ locale })),
        brandName,
        brandDisplayName: presentationBrandName,
        breadcrumbs,
        browseKey: 'brand',
        browseValue: brandBrowseName,
        category,
        constructorResponse: {},
        displayName: `${presentationBrandName} ${presentationTaxonomyLevel4 || presentationTaxonomyLevel3 || presentationSubcategory}`,
        globalPricingId,
        subCategory,
        taxonomyLevel3: taxonomyLevel3 || null,
        taxonomyLevel4: taxonomyLevel4 || null,
        isCrawler,
        consumerSearchInitialData: JSON.stringify({}),
        consumerSearchFilters,
        pageBreadcrumbs: [
          {
            name: presentationBrandName,
            slug: `/brand/${CORRECT_BRAND_NAME}`,
            isStatic: false,
          },
          {
            name: startCase(category),
            slug: `/${category}/brand/${CORRECT_BRAND_NAME}`,
            isStatic: !subCategory,
          },
          {
            name: startCase(subCategory || category),
            slug: `/${category}/brand/${CORRECT_BRAND_NAME}/${slugify(subCategory || category)}`,
            isStatic: !taxonomyLevel3,
          },
          ...(taxonomyLevel3
            ? [
                {
                  name: startCase(taxonomyLevel3),
                  slug: `/${category}/brand/${CORRECT_BRAND_NAME}/${slugify(subCategory || category)}/${slugify(taxonomyLevel3)}`,
                  isStatic: !taxonomyLevel4,
                },
              ]
            : []),
          ...(taxonomyLevel4
            ? [
                {
                  name: startCase(taxonomyLevel4),
                  slug: `/${category}/brand/${CORRECT_BRAND_NAME}/${slugify(subCategory || category)}/${slugify(
                    taxonomyLevel3 || '',
                  )}/${slugify(taxonomyLevel4)}`,
                  isStatic: true,
                },
              ]
            : []),
        ],
      },
    };
  } catch {
    return {
      notFound: true,
    };
  }
};

export default CategorizedBrandPage;
